import React, { useCallback, useEffect, useState } from 'react';

import { useMutation, gql } from '@nerdwallet/apollo';
import { useAnalytics } from '@lib/segment';

import { PlaidLinkLauncher } from './plaid-link';
import { usePlaidModalVisibility } from './plaid-modal-visibility-context';

const REINITIALIZE_FINANCIAL_PORTAL_MUTATION = gql`
  mutation ReinitializeFinancialPortalMutation(
    $financialPortalId: ID!
    $redirectUri: String
  ) {
    reinitializeFinancialPortal(
      financialPortalId: $financialPortalId
      redirectUri: $redirectUri
    ) {
      details {
        publicToken
        recommendedInstitutionId
        recommendedSourceInstitutionId
      }
      financialPortal {
        id
        primaryClientAction
      }
    }
  }
`;

/**
 * A wrapper around the plaid link step that handles reinitializing the portal
 * to get the public token before triggering Plaid Link. This component should
 * only be called to fix an existing portal in an ERROR state.
 */
export const PlaidRelink = ({ plaidModifiedLinkedAccounts }) => {
  const { showPlaidVisibility } = usePlaidModalVisibility();
  const { segmentPage } = useAnalytics();
  const [hasReinitialized, setHasReinitialized] = useState(false);
  const [publicToken, setPublicToken] = useState<string | null>(null);

  const [reinitializePortal] = useMutation(
    REINITIALIZE_FINANCIAL_PORTAL_MUTATION,
    {
      variables: {
        financialPortalId: showPlaidVisibility.selectedPortal?.id,
        redirectUri: `${process.env.NEXT_PUBLIC_INTERNAL_BASE_URL}/home/account-link/oauth-redirect`,
      },
      onCompleted: (data) => {
        setPublicToken(data?.reinitializeFinancialPortal?.details?.publicToken);
        setHasReinitialized(true);
      },
      onError: (error) => {
        throw error;
      },
      fetchPolicy: 'no-cache',
    },
  );

  const triggerReinitializePortal = useCallback(async () => {
    if (!hasReinitialized) {
      reinitializePortal();
    }
  }, [reinitializePortal, hasReinitialized]);

  useEffect(() => {
    if (showPlaidVisibility.selectedPortal?.id) {
      triggerReinitializePortal();
    }
  }, [showPlaidVisibility.selectedPortal?.id, triggerReinitializePortal]);

  useEffect(() => {
    if (showPlaidVisibility.selectedPortal?.marketplaceInstitution?.id) {
      segmentPage('relink_portal');
    }
  }, [
    segmentPage,
    showPlaidVisibility.selectedPortal?.marketplaceInstitution?.id,
  ]);

  return (
    <React.Fragment>
      <PlaidLinkLauncher
        plaidModifiedLinkedAccounts={plaidModifiedLinkedAccounts}
        relinkProps={{
          publicToken,
          financialPortalId: showPlaidVisibility.selectedPortal?.id,
          marketplaceInstitutionId:
            showPlaidVisibility.selectedPortal?.marketplaceInstitution?.id,
        }}
      />
    </React.Fragment>
  );
};

export default PlaidRelink;
